import { Component, effect, inject, OnDestroy } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormAutocompleteComponent, FormInputComponent, FormMultiSelectComponent, FormSelectComponent, FormTextareaComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { CountryCode, COUNTRY_OPTIONS } from '@shared/types';
import { FormOptionsFacade } from '@app/store/form-options';
import { SchoolFacade } from '@app/store/school';
import { ImportLearnersComponent } from '../view/import-learners/import-learners.component';

@Component({
    selector: 'app-school-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        FormAutocompleteComponent,
        FormInputComponent,
        FormMultiSelectComponent,
        FormSelectComponent,
        FormTextareaComponent,
        UnavailableComponent,
        ImportLearnersComponent,
    ],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly schoolFacade = inject(SchoolFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    readonly school = this.schoolFacade.school;
    readonly loading = this.schoolFacade.loading;
    readonly saving = this.schoolFacade.saving;
    readonly error = this.schoolFacade.error;
    readonly adminEmails = this.schoolFacade.adminEmails;
    readonly countryOptions = COUNTRY_OPTIONS;
    readonly courseOptions = this.formOptionsFacade.courses;

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        shortName: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        description: this.formBuilder.control(''),
        rollNumber: this.formBuilder.control('', [FormValidators.Text]),
        contactEmail: this.formBuilder.control('', [FormValidators.Text, FormValidators.Email]),
        courseIds: this.formBuilder.control<string[]>([]),
        address: this.formBuilder.group({
            street: this.formBuilder.control(''),
            postalCode: this.formBuilder.control(''),
            city: this.formBuilder.control(''),
            country: this.formBuilder.control<CountryCode | null>('IE', [FormValidators.Required]),
        }),
    });

    constructor() {
        this.formOptionsFacade.getCourses();

        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.schoolFacade.getSchool(id);
            this.schoolFacade.getSchoolAdmins(id);
        }

        effect(() => {
            const school = this.school();
            if (school) {
                this.form.patchValue(school);
            }
        });

        effect(() => {
            if (this.schoolFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.schoolFacade.saving() === false && this.schoolFacade.saved() === false) {
                this.form.enable();
            }
        });
    }

    ngOnDestroy(): void {
        this.schoolFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const school = this.school();
        if (this.form.valid && school) {
            this.form.disable();
            this.schoolFacade.updateSchool(school.id, this.form.getRawValue());
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
